import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Alert,
} from "@mui/material";
import { Add as AddIcon, Clear as ClearIcon } from "@mui/icons-material";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import { TrainingStep } from "../utils/enums";
import { useTheme } from "@mui/material/styles";

const AddTrainingChoiceDialog = ({ open, onClose, onSelectOption }) => {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "16px" }}>
        Add New Training Course
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.customColors.text.disabled,
          }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontSize: "14px" }}
          >
            Choose how you would like to add a new training course.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: "pointer",
                border: "1px solid",
                borderColor: theme.customColors.border.accent,
                borderRadius: "12px",
                background: theme.customColors.background.main,
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                transition: "all 0.2s ease",
                height: "100%",
                "&:hover": {
                  borderColor: theme.customColors.button.primary,
                  backgroundColor: theme.customColors.background.info,
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                },
              }}
              onClick={() => onSelectOption(TrainingStep.LIBRARY)}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: theme.customColors.status.info.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LocalLibraryOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: theme.customColors.button.primary,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        fontSize: "15px",
                        color: theme.customColors.text.primary,
                        mb: 0.5,
                      }}
                    >
                      Add from Library
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "12px", lineHeight: 1.3 }}
                    >
                      Select from our library of pre-defined training courses
                      with training material included.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: "auto", pt: 0.5 }}>
                  <Chip
                    label="Recommended"
                    size="small"
                    sx={{
                      fontSize: "10px",
                      height: 18,
                      backgroundColor: theme.customColors.status.success.bg,
                      color: theme.customColors.status.success.text,
                      fontWeight: 600,
                      borderRadius: "12px",
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                pointerEvents: "none",
                border: "1px dashed",
                borderColor: theme.customColors.border.disabled,
                borderRadius: "12px",
                backgroundColor: theme.customColors.background.tableRow,
                color: "text.disabled",
                boxShadow: "none",
                height: "100%",
                opacity: 1,
                transition: "none",
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: theme.customColors.status.warning.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AddIcon
                      sx={{
                        fontSize: 20,
                        color: theme.customColors.status.warning.alert,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{
                        fontSize: "15px",
                        color: theme.customColors.text.primary,
                        mb: 0.5,
                      }}
                    >
                      Create Manually (Coming soon)
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "12px", lineHeight: 1.3 }}
                    >
                      Create a custom training course with your resources and
                      training material.
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: "auto", pt: 0.5 }}>
                  <Chip
                    label="Coming Soon"
                    size="small"
                    sx={{
                      fontSize: "10px",
                      height: 18,
                      backgroundColor:
                        theme.customColors.background.lightGrayBg,
                      color: theme.customColors.text.disabled,
                      fontWeight: 600,
                      borderRadius: "12px",
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" sx={{ fontSize: "14px" }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTrainingChoiceDialog;
